/** Unity serialization helpers shared by the build tools and the browser (prefab override application). */

export interface URef {
  fileID: string;
  guid?: string;
  type?: string;
}

export function num(v: any, def = 0): number {
  if (v === undefined || v === null || v === '') return def;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

export function ref(v: any): URef | null {
  if (!v || typeof v !== 'object' || v.fileID === undefined) return null;
  return { fileID: String(v.fileID), guid: v.guid, type: v.type };
}

export function isNullRef(v: any): boolean {
  const r = ref(v);
  return !r || r.fileID === '0';
}

/** Unity uses 64-bit signed fileIDs; nested prefab objects get fileID = instanceId XOR sourceId. */
export function xorFileId(a: string, b: string): string {
  const mask = (1n << 64n) - 1n;
  const toU = (s: string) => BigInt(s) & mask;
  let r = toU(a) ^ toU(b);
  if (r >= 1n << 63n) r -= 1n << 64n;
  return r.toString();
}

/** Apply a Unity propertyPath (e.g. "m_Tiles.Array.data[3].second.m_TileIndex") to a parsed YAML object. */
export function applyPropertyPath(root: any, path: string, value: any, objectReference: any): void {
  const tokens = tokenizePath(path);
  let cur = root;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const last = i === tokens.length - 1;
    if (t.kind === 'size') return;
    if (t.kind === 'key') {
      const next = tokens[i + 1];
      if (next && next.kind === 'size') {
        if (!Array.isArray(cur[t.key])) cur[t.key] = [];
        const arr: any[] = cur[t.key];
        const n = num(value);
        if (arr.length > n) arr.length = n;
        while (arr.length < n) arr.push(arr.length ? structuredCloneSafe(arr[arr.length - 1]) : {});
        return;
      }
      if (next && next.kind === 'index') {
        if (!Array.isArray(cur[t.key])) cur[t.key] = [];
        cur = cur[t.key];
        continue;
      }
      if (last) {
        setLeaf(cur, t.key, value, objectReference);
        return;
      }
      if (cur[t.key] === undefined || cur[t.key] === null || typeof cur[t.key] !== 'object') cur[t.key] = {};
      cur = cur[t.key];
    } else if (t.kind === 'index') {
      const arr: any[] = cur;
      while (arr.length <= t.index) arr.push({});
      const next = tokens[i + 1];
      if (next && (next.kind === 'index' || next.kind === 'size')) {
        if (!Array.isArray(arr[t.index])) arr[t.index] = [];
        if (next.kind === 'size') {
          const inner: any[] = arr[t.index];
          const n = num(value);
          if (inner.length > n) inner.length = n;
          while (inner.length < n) inner.push(inner.length ? structuredCloneSafe(inner[inner.length - 1]) : {});
          return;
        }
        cur = arr[t.index];
        continue;
      }
      if (last) {
        setLeaf(arr, t.index, value, objectReference);
        return;
      }
      if (arr[t.index] === undefined || arr[t.index] === null || typeof arr[t.index] !== 'object') arr[t.index] = {};
      cur = arr[t.index];
    }
  }
}

function setLeaf(container: any, key: string | number, value: any, objectReference: any) {
  const existing = container[key];
  const orf = ref(objectReference);
  const existingIsRef = existing && typeof existing === 'object' && 'fileID' in existing;
  if ((orf && orf.fileID !== '0') || existingIsRef) {
    container[key] = orf && orf.fileID !== '0' ? { ...objectReference, fileID: orf.fileID } : { fileID: '0' };
  } else {
    container[key] = value;
  }
}

function structuredCloneSafe<T>(v: T): T {
  return v === undefined ? v : JSON.parse(JSON.stringify(v));
}

type Tok = { kind: 'key'; key: string } | { kind: 'index'; index: number } | { kind: 'size' };

export function tokenizePath(path: string): Tok[] {
  const out: Tok[] = [];
  const parts = path.split('.');
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p === 'Array') {
      const nxt = parts[i + 1] || '';
      if (nxt === 'size') {
        out.push({ kind: 'size' });
        i++;
        continue;
      }
      const mm = /^data\[(\d+)\]$/.exec(nxt);
      if (mm) {
        out.push({ kind: 'index', index: +mm[1] });
        i++;
        continue;
      }
    }
    out.push({ kind: 'key', key: p });
  }
  return out;
}

export function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

/** Unity writes empty sequences as `{}`; normalize to arrays. */
export function arr(v: any): any[] {
  return Array.isArray(v) ? v : [];
}
